<template>
  <div v-if="shouldShowHeader" class="home_header_container">
    <div class="college_wrapper" @click="showMaster">
      <img :src="collegeDetail.whiteLogoPreviewUrl" class="title_img" />
      <div class="title_separator"></div>

      <div class="select_text_wrapper">
        <p class="select_text">
          {{ collegeDetail.collegeName }}
        </p>
        <i class="iconfont icon-icon_shouye_xiala select_icon" :class="{ roate: masterShow }"></i>
      </div>
    </div>
    <div class="icon_wrapper" @click="searchHandle">
      <i class="iconfont icon-daohanglansousuo icon_item"></i>
    </div>
    <div class="icon_wrapper" @click="skipPage">
      <i class="iconfont icon-daohanglanxiaoxi icon_item"></i>
      <div class="badge_wrapper" v-show="count !== '0'">
        <span class="badge_text">{{ parseInt(count) > 99 ? '99+' : count }}</span>
      </div>
    </div>

    <div class="master" v-show="masterShow" @click="hideMaster" @touchmove="hideMaster">
      <Transition name="pull">
        <div class="select_wrapper" v-show="masterShow" @click.stop @touchmove.stop>
          <p
            class="select_item"
            v-for="item in collegeList"
            :key="item.id"
            :class="{ active: collegeId === item.id }"
            @click="selectCollege(item.id)"
          >
            {{ item.collegeName }}
          </p>
        </div>
      </Transition>
      <div class="triangle"></div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, onMounted, computed, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useThemeColor, useThemeColorWithAlpha } from '@/utils/theme'
import { CollegeDetail } from '@/api/Models/HomeConfig'
import { College } from '@/api/Models/CollegeList'
import { useUsersStore } from '@/stores/users'
import { storeToRefs } from 'pinia'
import EventSource from 'eventsource'
import { useAuthStore } from '@/stores/login'
import { useCommunityStore } from '@/stores/community'
import { isFeishu } from '@/utils/feishu'

defineProps<{
  collegeDetail: CollegeDetail
  collegeList: College[]
}>()
const emit = defineEmits(['setIdHandle'])
const tenantId = localStorage.getItem('sandsTalkTenantId')

// 替换原先从 localStorage 读取的 token，改为从 Pinia 派生，保持响应式
const authStore = useAuthStore()
const token = computed(() => authStore.getAccessToken)
const shouldShowHeader = computed(() => !isFeishu || !!token.value)

const count = ref('0')

// 防止重复建立 SSE 连接
const sseStarted = ref(false)
const getMsgCount = () => {
  if (sseStarted.value) return
  sseStarted.value = true

  const { getAccessToken } = useAuthStore()
  const evtSource = new EventSource(process.env.VUE_APP_API_URL_SSE + '/sands-system/api/v1/sse/create?bizType=0', {
    headers: {
      Authorization: `Bearer ${getAccessToken}`,
      'tenant-id': tenantId || ''
    }
  })
  const listener = function (event: { type: string; data: string }) {
    if (event.type === 'message') {
      count.value = event.data
    }
  }
  evtSource.addEventListener('open', listener)
  evtSource.addEventListener('message', listener)
  evtSource.addEventListener('error', listener)
}

// 延迟创建 SSE：仅在 token 可用或非飞书环境时触发
onMounted(() => {
  if (!isFeishu || !!token.value) {
    getMsgCount()
  }
})

// 监听 token，在飞书环境下 token 到位后再建立 SSE
watch(token, (val) => {
  if (isFeishu && val) {
    getMsgCount()
  }
})

const router = useRouter()
const searchHandle = () => {
  router.push('/courseSearch')
}

const usersStore = useUsersStore()
const { collegeId } = storeToRefs(usersStore)
const selectCollege = (id: string) => {
  emit('setIdHandle', id)
  masterShow.value = false
  const communityStore = useCommunityStore()
  communityStore.setIsRefresh(true)
}

const skipPage = () => {
  router.push('/news')
}

const themeColor = useThemeColor()
const themeColorWithAlpha = useThemeColorWithAlpha(0.08)

const masterShow = ref()
const hideMaster = () => {
  masterShow.value = false
}
const showMaster = () => {
  masterShow.value = true
}
</script>

<style lang="less" scoped>
@import url('@/styles/variables.less');
.pull-enter-active {
  animation: bounce-in 0.2s;
  transform-origin: top;
}
.pull-leave-active {
  animation: bounce-in 0.2s reverse;
  transform-origin: top;
}
@keyframes bounce-in {
  0% {
    transform: scaleY(0);
  }
  100% {
    transform: scaleY(1);
  }
}

.home_header_container {
  width: 100%;
  height: 44px;
  position: fixed;
  left: 0;
  top: 0;
  background-color: v-bind(themeColor);
  z-index: 999;
  display: flex;
  align-items: center;
  padding: 0 16px;
  .college_wrapper {
    flex: 1;
    display: flex;
    align-items: center;
  }
  .master {
    position: fixed;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    z-index: 2000;
    .triangle {
      width: 0px;
      height: 0px;
      box-sizing: content-box;
      border: 8px solid transparent;
      border-bottom: 8px solid @contentBackground;
      position: absolute;
      top: 25px;
      right: 175px;
      z-index: 1001;
    }
  }
  .select_wrapper {
    position: absolute;
    width: 180px;
    max-height: 200px;
    background-color: @contentBackground;
    top: 40px;
    right: 153px;
    z-index: 1000;
    border-radius: 4px;
    overflow-y: auto;
    box-shadow: 0 2px 8px 0 rgba(0, 0, 0, 0.1);
    .select_item {
      height: 40px;
      padding: 0 10px;
      text-align: center;
      line-height: 40px;
      font-size: 14px;
      color: #999;
      .text_etc;
      &.active {
        color: v-bind(themeColor);
        background-color: v-bind(themeColorWithAlpha);
      }
    }
  }

  .title_img {
    max-width: 76px;
    height: 21px;
    flex: 0 0 auto;
  }

  .title_separator {
    width: 4px;
    height: 4px;
    margin: 0 6px;
    background-color: @textWhite;
    border-radius: 50%;
    flex: 0 0 auto;
  }

  .select_text_wrapper {
    width: 0;
    flex: 1;
    display: flex;
    align-items: center;
  }

  .select_text {
    color: @textWhite;
    font-size: 16px;
    font-weight: bold;
    .text_etc;
  }

  .select_icon {
    font-size: 16px;
    margin-right: 12px;
    color: @textWhite;
    &.roate {
      transform: rotate(180deg);
    }
  }

  .icon_wrapper {
    flex: 0;
    position: relative;
    margin-right: 10px;

    .icon_item {
      font-size: 18px;
      color: @textWhite;
    }

    .badge_wrapper {
      min-width: 14px;
      height: 14px;
      padding: 0 2px;
      position: absolute;
      top: -4px;
      left: 11px;
      background-color: #ec381b;
      border-radius: 7px;
      border: 1px solid #fff;
      .flex_center;

      .badge_text {
        font-size: 12px;
        color: @textWhite;
        display: block;
        transform: scale(0.833);
      }
    }
  }
}
</style>
